import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader, Alert} from 'reactstrap';
import {observable, computed, action} from 'mobx';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCircleNotch, faChevronDown, faChevronUp} from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/lib/Creatable';
import uuid from 'uuid/v4';

import inject from '../../hoc/injectHoc';
import {STATE_FULFILLED, STATE_PENDING, STATE_REJECTED} from '../../../constants/asyncConstants';
import {
  GENERATE_IMAGE_THEME_OPTIONS,
  GENERATE_IMAGE_STYLES,
  GENERATE_IMAGE_TEXT_PLACEMENT_OPTIONS,
  GENERATE_IMAGE_CONTENT_DENSITY_OPTIONS,
  GENERATE_IMAGE_DEFAULT_AVOID_ITEMS,
} from './options';
import PreviewImageModal from '../previewImageModal/PreviewImageModal';

import './generateImageModal.scss';

/**
 * Converts a base64 data URI into a File.
 *
 * @param {string} dataUri
 * @returns {File}
 */
const dataUriToFile = (dataUri) => {
  const [header, base64Data] = dataUri.split(',');
  const mimeType = header.match(/data:(.*);base64/)[1];
  const fileExtension = mimeType.split('/').pop();
  const binaryString = atob(base64Data);
  const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));

  return new File([bytes], `generated-image-${uuid()}.${fileExtension}`, {type: mimeType});
};

/**
 * The GenerateImageModal component.
 */
class GenerateImageModal extends React.Component {
  /**
   * form data
   *
   * @type {Object}
   */
  @observable form = {
    subject: '',
    style: '',
    themeValue: '',
    themeCategory: '',
    textPlacement: GENERATE_IMAGE_TEXT_PLACEMENT_OPTIONS[0],
    contentDensity: GENERATE_IMAGE_CONTENT_DENSITY_OPTIONS[0],
    avoidItems: GENERATE_IMAGE_DEFAULT_AVOID_ITEMS.slice(),
  };

  /**
   * url to preview
   *
   * @type {string|null}
   */
  @observable selectedUrl = null;

  /**
   * Whether or not the advanced options group is open.
   *
   * @type {boolean}
   */
  @observable isAdvancedOptionsOpen = false;

  /**
   * Tracks if the form is disabled
   *
   * @returns {boolean}
   */
  @computed get isSubmitDisabled() {
    const {apiGenerateImageStore} = this.props;

    return Boolean(this.form.subject === '' || apiGenerateImageStore.getState() === STATE_PENDING);
  }

  /**
   * Tracks if the cancel button should be disabled
   *
   * @returns {boolean}
   */
  @computed get isCancelDisabled() {
    const {apiGenerateImageStore} = this.props;

    return Boolean(apiGenerateImageStore.getState() === STATE_PENDING);
  }

  /**
   * input & select onChange handler
   *
   * @param {{}} changeEvent
   */
  @action onChange = (changeEvent) => {
    const target = changeEvent.target;
    const value = target.value;

    if (typeof this.form[target.name] !== 'undefined') {
      this.form[target.name] = value;
    }
  };

  /**
   * Submit form event
   *
   * @param {{}} submitEvent
   */
  @action onSubmit = (submitEvent) => {
    const {apiGenerateImageStore, aspectRatio} = this.props;

    submitEvent.preventDefault();

    apiGenerateImageStore.makeRequest({
      subject: this.form.subject,
      style: this.form.style,
      themeCategory: this.form.themeCategory,
      themeValue: this.form.themeValue,
      textPlacement: this.form.textPlacement,
      contentDensity: this.form.contentDensity,
      avoidItems: this.form.avoidItems.slice(),
      aspectRatio,
    });
  }

  /**
   * Theme select onChange handler
   *
   * @param {?{value: string, category: string}} selected
   */
  @action onThemeChange = (selected) => {
    this.form.themeValue = selected ? selected.value : '';
    this.form.themeCategory = selected ? selected.category : '';
  }

  /**
   * Avoid/exclude select onChange handler
   *
   * @param {Array<{value: string}>} selected
   */
  @action onAvoidItemsChange = (selected) => {
    const values = (selected || []).map((option) => option.value).filter(Boolean);

    this.form.avoidItems.replace(values);
  }

  /**
   * Toggles the advanced options group.
   */
  @action onToggleAdvancedOptions = () => {
    this.isAdvancedOptionsOpen = !this.isAdvancedOptionsOpen;
  }

  /**
   * On preview image click
   *
   * @param {string} url
   */
  @action onImagePreviewClick = (url) => {
    this.selectedUrl = url;
  }

  /**
   * On hide preview modal
   */
  @action onHidePreviewImageModal = () => {
    this.selectedUrl = null;
  }

  /**
   * On use image from preview modal
   *
   * @param {string} url
   */
  @action onUseImage = (url) => {
    const {onImageSelect} = this.props;
    this.onHidePreviewImageModal();
    onImageSelect(dataUriToFile(url));
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {isOpen, onCancel, apiGenerateImageStore} = this.props;

    const areResultsOrErrorAvailable = apiGenerateImageStore.getState() === STATE_FULFILLED || apiGenerateImageStore.getState() === STATE_REJECTED;

    return (
      <Modal
        className="generate-image-modal"
        isOpen={isOpen}
        backdrop={true}
        toggle={onCancel}
        size={'lg'}
      >
        <ModalHeader className="text-dark-blue">
          Create a background for your sign
        </ModalHeader>
        <ModalBody>
          <h5 className="mb-3">Background Image Generator</h5>
          <form
            id="generate-image-form"
            onSubmit={this.onSubmit}
          >
            <div className="row">
              <div
                className={classNames(
                  'col-12',
                  {
                    'col-md-8': areResultsOrErrorAvailable,
                  }
                )}
              >
                <div className="form-group">
                  <label htmlFor="generate-image-subject">Subject</label>
                  <input
                    id="generate-image-subject"
                    name="subject"
                    className="form-control form-control-sm"
                    aria-describedby="generate-image-subject-help"
                    value={this.form.subject}
                    onChange={this.onChange}
                  />
                  <small
                    id="generate-image-subject-help"
                    className="form-text text-muted"
                  >
                    The subject is what you want to see in the image.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="generate-image-style">Style</label>
                  <select
                    id="generate-image-style"
                    name="style"
                    className="form-control form-control-sm"
                    aria-describedby="generate-image-style-help"
                    value={this.form.style}
                    onChange={this.onChange}
                  >
                    <option value="">-</option>
                    {GENERATE_IMAGE_STYLES.map((value) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {value}
                      </option>
                    ))}
                  </select>
                  <small
                    id="generate-image-style-help"
                    className="form-text text-muted"
                  >
                    The style refers to the artistic style of the image.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="generate-image-theme">Theme</label>
                  <ReactSelect
                    inputId="generate-image-theme"
                    name="theme"
                    className="site-select theme-light"
                    classNamePrefix="react-select"
                    isClearable={true}
                    placeholder="-"
                    value={this.form.themeValue
                      ? {value: this.form.themeValue, label: this.form.themeValue, category: this.form.themeCategory}
                      : null}
                    onChange={this.onThemeChange}
                    options={GENERATE_IMAGE_THEME_OPTIONS}
                  />
                </div>

                <div className="advanced-options-toggle mb-2">
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={this.onToggleAdvancedOptions}
                  >
                    Advanced Options
                    <FontAwesomeIcon
                      className="ml-2"
                      icon={this.isAdvancedOptionsOpen ? faChevronUp : faChevronDown}
                    />
                  </button>
                </div>

                {this.isAdvancedOptionsOpen && (
                  <div className="advanced-options">
                    <div className="form-group">
                      <label htmlFor="generate-image-text-placement">Text Placement</label>
                      <select
                        id="generate-image-text-placement"
                        name="textPlacement"
                        className="form-control form-control-sm"
                        value={this.form.textPlacement}
                        onChange={this.onChange}
                      >
                        {GENERATE_IMAGE_TEXT_PLACEMENT_OPTIONS.map((value) => (
                          <option
                            key={value}
                            value={value}
                          >
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="generate-image-content-density">Content Density</label>
                      <select
                        id="generate-image-content-density"
                        name="contentDensity"
                        className="form-control form-control-sm"
                        value={this.form.contentDensity}
                        onChange={this.onChange}
                      >
                        {GENERATE_IMAGE_CONTENT_DENSITY_OPTIONS.map((value) => (
                          <option
                            key={value}
                            value={value}
                          >
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="generate-image-avoid-items">Avoid / Exclude</label>
                      <CreatableSelect
                        inputId="generate-image-avoid-items"
                        name="avoidItems"
                        className="site-select theme-light"
                        classNamePrefix="react-select"
                        isClearable={false}
                        isMulti={true}
                        placeholder="Hit enter after each typed item"
                        value={this.form.avoidItems.map((value) => ({value, label: value}))}
                        onChange={this.onAvoidItemsChange}
                        options={[]}
                      />
                    </div>
                  </div>
                )}
              </div>
              {apiGenerateImageStore.case({
                fulfilled: (data) => {
                  const results = (data && data.results) || [];
                  return (
                    <div className="col-12 col-md-4">
                      <div className="mb-1">
                        <h6 className="mb-0">Results</h6>
                        <small>(Click image to see preview)</small>
                      </div>
                      {(results.length !== 0)
                        ? (
                          <div className="row image-preview-row">
                            {results.map((result) => {
                              const imageUrl = `data:image/png;base64,${result}`;

                              return (
                                <div
                                  key={result}
                                  className="col col-6 col-sm-12"
                                >
                                  <img
                                    className="result-thumbnail cursor-pointer"
                                    src={imageUrl}
                                    onClick={() => this.onImagePreviewClick(imageUrl)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )
                        : (
                          <span className="text-muted">No results found. Please try different parameters.</span>
                        )}
                    </div>
                  );
                },
                rejected: () => (
                  <div className="col-12 col-md-4">
                    <Alert
                      className="mb-0"
                      color="danger"
                    >
                      There was an error generating the image. Please try again.
                    </Alert>
                  </div>
                ),
              })}

              {(this.selectedUrl) && (
                <PreviewImageModal
                  isOpen={true}
                  onCancel={this.onHidePreviewImageModal}
                  onSubmitClick={this.onUseImage}
                  url={this.selectedUrl}
                />
              )}
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-light"
            type="button"
            onClick={onCancel}
            disabled={this.isCancelDisabled}
          >
            Cancel
          </button>
          <button
            className="btn btn-dark-blue"
            type="submit"
            form="generate-image-form"
            disabled={this.isSubmitDisabled}
          >
            {(apiGenerateImageStore.getState() === STATE_PENDING)
              ? (<>
                <FontAwesomeIcon
                  className="mr-2"
                  spin
                  icon={faCircleNotch}
                />
                Generating
              </>)
              : (<>Generate Backgrounds</>)
            }
          </button>
        </ModalFooter>
      </Modal>
    );
  }
}

GenerateImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onImageSelect: PropTypes.func.isRequired,
  apiGenerateImageStore: MobxPropTypes.observableObject,
  aspectRatio: PropTypes.string,
};

GenerateImageModal.defaultProps = {
  aspectRatio: '16:9',
};

GenerateImageModal.wrappedComponent = {};
GenerateImageModal.wrappedComponent.propTypes = {
  apiGenerateImageStore: MobxPropTypes.observableObject,
};

export default inject(GenerateImageModal)(
  observer(GenerateImageModal)
);
